import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getFormattedValue, PeriodGroup, getIntervalLabelsForData, shouldPointBeBlue, getPeriodTypeFromDuration } from '../utils';

type MiniBarChartProps = {
	history?: { values: number[] };
	period: PeriodGroup;
	type: string;
	labels?: string[];
	historyDepth?: number;
};

const MiniBarChart = ({
	history,
	period,
	type,
	labels: propLabels,
	historyDepth
}: MiniBarChartProps) => {

	const recentAbsolutes = history?.values || [];

	const periodType = period && period.firstPeriode ? getPeriodTypeFromDuration(period.firstPeriode.start, period.firstPeriode.end) : 'Last7Days';

	const labels = propLabels || ((period && period.firstPeriode && period.firstPeriode.end && recentAbsolutes.length > 0)
		? getIntervalLabelsForData(period.firstPeriode.end, periodType, recentAbsolutes.length, historyDepth)
		: []);



	const displayLabels = labels.map(label => label.includes(' - ') ? label.split(' - ')[0] : label);

	const [chartOptions, setChartOptions] = useState({
		chart: {
			type: 'column',
			height: 100
		},
		title: {
			text: '',
		},
		xAxis: {
			categories: [] as string[],
			labels: {
				enabled: false
			},
			gridLineWidth: 0
		},
		yAxis: {
			title: { text: '' },
			labels: {
				enabled: false
			},
			gridLineWidth: 0
		},
		series: [
			{
				data: [] as Highcharts.PointOptionsObject[],
				type: 'column', 
				color: '#C8C9D3',
				marker: {
					enabled: false
				},
				states: {
					hover: {
						enabled: false
					}
				}
			} as Highcharts.SeriesColumnOptions
		],
		credits: {
			enabled: false
		},
		legend: {
			enabled: false
		},
		exporting: {
			enabled: false
		},
		tooltip: {
			enabled: true
		}
	});

	useEffect(() => {
		if (recentAbsolutes && recentAbsolutes.length > 1) {

			const points = recentAbsolutes.map((val, idx) => {
				let color = '#C8C9D3';
				
				if (idx === recentAbsolutes.length - 1) {
					const prev = recentAbsolutes[recentAbsolutes.length - 2];
					color = val > prev ? '#2ECC40' : '#FF4136';
				}
				else if (shouldPointBeBlue(idx, recentAbsolutes.length, historyDepth)) {
					color = '#5174ED'; 
				}
				
				return {
					y: Number(Number(val).toFixed(2)),
					color: color,
					x: idx
				};
			});

			const zones = recentAbsolutes.map((val, idx) => {
				let color = '#C8C9D3';
				
				if (idx === recentAbsolutes.length - 1) {
					const prev = recentAbsolutes[recentAbsolutes.length - 2];
					color = val > prev ? '#2ECC40' : '#FF4136';
				}
				else if (shouldPointBeBlue(idx, recentAbsolutes.length, historyDepth)) {
					color = '#5174ED'; 
				}
				
				return {
					value: idx,
					color: color
				};
			});

			setChartOptions(prev => ({
				...prev,
				xAxis: {
					...prev.xAxis,
					categories: displayLabels as string[]
				},
				series: [
					{
						data: points,
						type: 'column',
						pointPadding: 0,
						groupPadding: 0,
						borderWidth: 2,
						color: '#C8C9D3',
						zones: zones,
						zoneAxis: 'x',
						states: {
							hover: {
								enabled: true
							}
						}
					} as Highcharts.SeriesColumnOptions
				],
				tooltip: {
					enabled: true,
					formatter: function(this: Highcharts.TooltipFormatterContextObject) {
						const idx = this.point.x;
						// On affiche le label complet (ex: '30 Jun')
						const date = displayLabels[idx] || `Intervalle ${Number(idx)+1}`;
						return `<b>${date}</b><br/>Value: <b>${ getFormattedValue (this.y, null, type)}</b>`;
					}
				}
			}));

		}
	}, [recentAbsolutes, period, type]);

	return (
		<div>
			<HighchartsReact
				highcharts={Highcharts}
				options={chartOptions}
			/>
		
		</div>
	);
};

export default MiniBarChart;
